// Constructeur de box.
//
// Le site ne vend plus de bonbon seul : ce qui est paye, c'est la taille
// de la box. Le script ne manipule donc aucun prix de bonbon, seulement
// des portions. Une portion vaut cent grammes, ou une piece pour les
// bonbons vendus a l'unite.
//
// Les premiers bonbons sont deja dans la page ; l'index complet arrive
// ensuite par la Section Rendering API et alimente la recherche, les
// filtres, « Voir plus » et le remplissage automatique.

const CLE = 'mixo:box';
const PORTION = 100;
const sobre = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

// \p{Diacritic} evite d'ecrire des caracteres combinants invisibles.
const sansAccent = (s) =>
  String(s).normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

const lisJson = (brut, defaut) => {
  try { return JSON.parse(brut); } catch { return defaut; }
};

class MxMix extends HTMLElement {
  connectedCallback() {
    this.parLot = Number(this.dataset.parLot || 48);
    this.format = this.dataset.formatMonnaie;
    this.mixes = lisJson(this.dataset.mixes, []);

    // Les bonbons des sept mix arrivent avec la page, nom et visuel
    // compris. On les retient : une box pre-remplie doit pouvoir
    // s'afficher sans attendre l'index des cinq cents references.
    this.connus = new Map();
    this.mixes.forEach((m) => (m.bonbons || []).forEach((b) => {
      if (b && b.h) this.connus.set(b.h, b);
    }));
    this.styles = lisJson(this.dataset.styles, []);
    this.mixDuMoment = this.dataset.mixDuMoment || '';
    this.capaciteDefaut = Number(this.dataset.capaciteDefaut || 0);

    this.grille = this.querySelector('[data-grille]');
    this.sachet = this.querySelector('[data-sachet]');

    // Index complet, rempli des que les lots arrivent.
    this.index = [];
    this.indexPret = false;

    // Bonbons affiches, par handle.
    this.cartes = new Map();
    this.recenseCartes();

    this.etat = { ordre: [], portions: new Map() };
    this.taille = null;
    this.filtre = '';
    this.requete = '';
    this.affiches = this.cartes.size;

    this.brancheTailles();
    this.brancheActions();
    this.brancheRecherche();
    this.brancheFiltres();

    this.restaure();
    this.preAjoutParUrl();
    this.dessine();

    // L'index des cinq cents bonbons part quand le fil principal se
    // libere : les quarante-huit premiers suffisent a utiliser la page,
    // et le charger tout de suite retardait l'interactivite. Une
    // interaction avec la recherche, les filtres ou le remplissage
    // automatique le reclame aussitot.
    this.demandeIndex();
  }

  recenseCartes() {
    this.querySelectorAll('[data-bonbon]').forEach((el) => {
      this.cartes.set(el.dataset.handle, {
        el,
        nom: el.dataset.nom,
        image: el.dataset.image,
        tags: el.dataset.tags || '',
        piece: el.hasAttribute('data-piece'),
      });
    });
  }

  /* ================= Chargement de l'index ================= */
  demandeIndex() {
    if (this.indexFini) return this.indexFini;

    this.indexFini = new Promise((resolve) => {
      const lance = () => this.chargeIndex().then(resolve);
      if ('requestIdleCallback' in window) {
        requestIdleCallback(lance, { timeout: 2500 });
      } else {
        setTimeout(lance, 400);
      }
    });
    return this.indexFini;
  }

  async chargeIndex() {
    try {
      const premiere = await this.litPage(1);
      if (!premiere) return;

      this.index = premiere.bonbons;

      // Les pages suivantes partent ensemble : trois requetes au total
      // pour cinq cents references, pas une par page en serie.
      const restantes = [];
      for (let p = 2; p <= premiere.pages; p++) restantes.push(this.litPage(p));

      for (const lot of await Promise.all(restantes)) {
        if (lot) this.index.push(...lot.bonbons);
      }

      this.indexPret = true;
      this.majVoirPlus();

      // L'index peut nommer des bonbons qu'aucune carte ne portait : la
      // box doit etre redessinee pour les faire apparaitre.
      this.dessine();
    } catch (e) {
      // Sans index, les premiers bonbons restent utilisables.
      console.error('Constructeur : index indisponible', e);
    }
  }

  async litPage(numero) {
    const url = new URL(this.dataset.urlIndex, location.origin);
    url.searchParams.set('page', numero);
    const rep = await fetch(url);
    if (!rep.ok) return null;

    // La Section Rendering API enveloppe le JSON dans du HTML de section.
    const texte = await rep.text();
    const debut = texte.indexOf('{');
    const fin = texte.lastIndexOf('}');
    if (debut < 0 || fin < 0) return null;
    return JSON.parse(texte.slice(debut, fin + 1));
  }

  /* ================= Tailles ================= */
  brancheTailles() {
    this.boutonsTaille = [...this.querySelectorAll('[data-taille]')];
    if (!this.boutonsTaille.length) return;

    this.tailles = this.boutonsTaille.map((b) => this.litTaille(b));
    const phare = this.tailles.find((t) => t.capacite === this.capaciteDefaut);
    this.poseTaille(phare ?? this.tailles[0]);

    this.boutonsTaille.forEach((bouton) => {
      bouton.addEventListener('click', () => this.demandeTaille(this.litTaille(bouton)));
    });
  }

  litTaille(bouton) {
    return {
      bouton,
      id: bouton.dataset.variante,
      nom: bouton.querySelector('.mx-taille__nom')?.textContent.trim() ?? '',
      capacite: Number(bouton.dataset.capacite),
      bonus: Number(bouton.dataset.bonus),
      prix: Number(bouton.dataset.prix),
    };
  }

  poseTaille(taille) {
    this.taille = taille;
    this.boutonsTaille.forEach((b) =>
      b.setAttribute('aria-pressed', String(b === taille.bouton))
    );
  }

  capaciteEnPortions(taille = this.taille) {
    return Math.floor((taille?.capacite ?? 0) / PORTION);
  }

  /* Changer de taille ne doit jamais defaire le travail de quelqu'un
     sans le lui demander : on simule, et on ne touche a rien tant que la
     reponse n'est pas venue. */
  demandeTaille(taille) {
    if (taille.capacite === this.taille?.capacite) return;

    const simulation = this.simuleReduction(taille);
    if (!simulation.retirees) {
      this.poseTaille(taille);
      this.dessine();
      this.sauvegarde();
      return;
    }

    this.tailleEnAttente = { taille, simulation };
    const texte = this.querySelector('[data-confirme-texte]');
    if (texte) texte.textContent = this.texteDeReduction(taille, simulation);
    const panneau = this.querySelector('[data-confirme]');
    if (panneau) {
      panneau.hidden = false;
      panneau.querySelector('[data-confirme-oui]')?.focus();
    }
  }

  texteDeReduction(taille, simulation) {
    const g = simulation.retirees * PORTION;
    return `Cette taille est plus petite : ${g} g seront retirés de ta box, `
      + 'en commençant par les derniers ajoutés.';
  }

  confirmeTaille(oui) {
    const panneau = this.querySelector('[data-confirme]');
    if (panneau) panneau.hidden = true;

    const attente = this.tailleEnAttente;
    this.tailleEnAttente = null;
    if (!oui || !attente) return;

    this.etat.ordre = attente.simulation.ordre;
    this.etat.portions = attente.simulation.portions;
    this.poseTaille(attente.taille);
    this.alerte(`Taille changée. ${attente.simulation.retirees * PORTION} g retirés.`);
    this.dessine();
    this.sauvegarde();
  }

  /* Simule le passage a une taille, sans rien modifier : la reponse
     sert a demander avant de defaire. On retire toujours les dernieres
     portions ajoutees. */
  simuleReduction(taille) {
    const ordre = [...this.etat.ordre];
    const portions = new Map(this.etat.portions);
    const somme = () => [...portions.values()].reduce((s, n) => s + n, 0);
    const capacite = Math.floor(taille.capacite / PORTION);

    let retirees = 0;
    while (somme() > capacite && ordre.length) {
      const h = ordre[ordre.length - 1];
      const reste = (portions.get(h) || 0) - 1;
      if (reste > 0) {
        portions.set(h, reste);
      } else {
        portions.delete(h);
        ordre.pop();
      }
      retirees++;
    }

    return { ordre, portions, retirees };
  }

  /* ================= Contenu de la box ================= */
  portionsPosees(filtre) {
    return [...this.etat.portions]
      .filter(([h]) => !filtre || filtre(h))
      .reduce((s, [, n]) => s + n, 0);
  }

  total() {
    return this.portionsPosees() * PORTION;
  }

  infos(handle) {
    const carte = this.cartes.get(handle);
    if (carte) return carte;
    const entree = this.index.find((b) => b.h === handle) ?? this.connus.get(handle);
    if (!entree) return null;
    return {
      nom: entree.n,
      image: entree.i,
      tags: entree.t,
      piece: entree.u === 1,
    };
  }

  /* Une seule limite : la capacite de la box. */
  obstacle() {
    return this.portionsPosees() + 1 > this.capaciteEnPortions() ? 'pleine' : null;
  }

  ajoute(handle, depuis) {
    const info = this.infos(handle);
    if (!info || !this.taille) return false;

    if (this.obstacle()) {
      this.alerte('Ta box est pleine.');
      return false;
    }

    if (!this.etat.portions.has(handle)) this.etat.ordre.push(handle);
    this.etat.portions.set(handle, (this.etat.portions.get(handle) || 0) + 1);

    const cible = this.querySelector('[data-cible-chute]');
    if (depuis) this.faisTomber(depuis, cible);
    if (cible) this.rebondis(cible);

    this.dessine();
    this.sauvegarde();
    return true;
  }

  retire(handle) {
    const reste = (this.etat.portions.get(handle) || 0) - 1;
    if (reste > 0) {
      this.etat.portions.set(handle, reste);
    } else {
      this.etat.portions.delete(handle);
      this.etat.ordre = this.etat.ordre.filter((h) => h !== handle);
    }
    this.dessine();
    this.sauvegarde();
  }

  vide() {
    this.etat.ordre = [];
    this.etat.portions.clear();
    this.alerte('Box vidée.');
    this.dessine();
    this.sauvegarde();
  }

  /* ================= Actions ================= */
  brancheActions() {
    this.addEventListener('click', (ev) => {
      const plus = ev.target.closest('[data-ajouter-un]');
      if (plus) {
        const carte = plus.closest('[data-bonbon]');
        this.ajoute(plus.dataset.ajouterUn, carte?.querySelector('.mx-photo'));
        return;
      }

      const moins = ev.target.closest('[data-retirer]');
      if (moins) { this.retire(moins.dataset.retirer); return; }

      if (ev.target.closest('[data-vider]')) { this.vide(); return; }
      if (ev.target.closest('[data-confirme-oui]')) { this.confirmeTaille(true); return; }
      if (ev.target.closest('[data-confirme-non]')) { this.confirmeTaille(false); return; }
      if (ev.target.closest('[data-partir-dun-mix]')) { this.ouvreChoix('mix'); return; }
      if (ev.target.closest('[data-completer]')) { this.ouvreChoix('style'); return; }
      if (ev.target.closest('[data-completer-raccourci]')) { this.ouvreChoix('style'); return; }
      if (ev.target.closest('[data-ferme-choix]') || ev.target.closest('[data-voile-choix]')) {
        this.fermeChoix();
        return;
      }
      if (ev.target.closest('[data-monter]')) { this.monteDeTaille(); return; }
      if (ev.target.closest('[data-relance-oui]')) { this.repondAlaRelance(true); return; }
      if (ev.target.closest('[data-relance-non]')) { this.repondAlaRelance(false); return; }
      if (ev.target.closest('[data-ajoute-additionnel]')) { this.ajouteAdditionnel(); return; }
      if (ev.target.closest('[data-voir-plus]')) { this.demandeIndex(); this.ajouteLot(); return; }

      const choixMix = ev.target.closest('[data-prend-mix]');
      if (choixMix) { this.partirDunMix(choixMix.dataset.prendMix); return; }

      const choixStyle = ev.target.closest('[data-prend-style]');
      if (choixStyle) { this.completePourMoi(Number(choixStyle.dataset.prendStyle)); return; }

      const ajouter = ev.target.closest('[data-ajouter]');
      if (ajouter && !ajouter.disabled) this.ajouteAuPanier();
    });

    const bascule = this.querySelector('[data-bascule]');
    bascule?.addEventListener('click', () => {
      const ouvert = this.sachet.dataset.ouvert === 'true';
      this.poseLeTiroir(!ouvert);
    });

    document.addEventListener('keydown', (ev) => {
      if (ev.key !== 'Escape') return;
      if (!this.querySelector('[data-choix-mix]')?.hidden) this.fermeChoix();
      else if (!this.querySelector('[data-choix-style]')?.hidden) this.fermeChoix();
      else if (!this.querySelector('[data-confirme]')?.hidden) this.confirmeTaille(false);
    });
  }

  poseLeTiroir(ouvert) {
    if (!this.sachet) return;
    this.sachet.dataset.ouvert = String(ouvert);
    this.querySelector('[data-bascule]')?.setAttribute('aria-expanded', String(ouvert));
  }

  ouvreLeTiroir() { this.poseLeTiroir(true); }

  /* ================= Rendu ================= */
  dessine() {
    const posees = this.portionsPosees();
    const capacite = this.capaciteEnPortions();
    const t = posees * PORTION;
    const cible = capacite * PORTION;
    const reste = cible - t;
    const pct = capacite > 0 ? Math.min(100, Math.round((posees / capacite) * 100)) : 0;
    const plein = reste <= 0;

    this.querySelectorAll('[data-poids-actuel]').forEach((el) => (el.textContent = t));
    this.querySelectorAll('[data-poids-cible]').forEach((el) => (el.textContent = cible));
    this.querySelectorAll('[data-jauge]').forEach((el) => (el.style.width = `${pct}%`));

    // Le bonus occupe un segment proportionnel, colle a la fin de la
    // piste : il s'ajoute a la box, il ne se remplit pas.
    const bonus = this.taille?.bonus ?? 0;
    const segment = this.querySelector('[data-segment-bonus]');
    const piste = this.querySelector('[data-piste]');
    if (segment && piste) {
      segment.hidden = bonus <= 0;
      segment.textContent = bonus > 0 ? `+${bonus} g` : '';
      piste.style.flexGrow = String(cible || 1);
      segment.style.flexGrow = String(bonus || 0);
    }

    const jauge = this.querySelector('[data-jauge-role]');
    if (jauge) {
      jauge.setAttribute('aria-valuenow', pct);
      jauge.setAttribute('aria-valuetext', `${t} grammes sur ${cible}`);
    }

    this.querySelectorAll('[data-reste]').forEach((el) => {
      el.textContent = plein
        ? (bonus > 0 ? `Box pleine, +${bonus} g offerts` : 'Ta box est pleine')
        : `Il te reste ${reste} g à choisir`;
    });

    this.sachet?.classList.toggle('mx-sachet--plein', plein);
    this.sachet?.classList.toggle('mx-sachet--vide', t === 0);

    const bouton = this.querySelector('[data-ajouter]');
    if (bouton) {
      bouton.disabled = !plein;
      bouton.textContent = plein
        ? `Ajouter au panier · ${this.monnaie(this.taille?.prix ?? 0)}`
        : `Il te reste ${reste} g à choisir`;
    }
    const raccourci = this.querySelector('[data-completer-raccourci]');
    if (raccourci) raccourci.hidden = plein || t === 0;

    // La box vient d'etre remplie : on ouvre le tiroir. C'est le moment
    // ou le client a fini, et tout ce qui compte y est — le recapitulatif,
    // la montee en taille, l'additionnel et le bouton de paiement. Replie,
    // ce panneau n'aurait ete vu par personne. Une seule fois, pour ne pas
    // rouvrir sans cesse un tiroir qu'on vient de fermer.
    if (plein && !this.etaitPleine && this.sachet?.dataset.ouvert === 'false') {
      this.ouvreLeTiroir();
    }
    this.etaitPleine = plein;

    this.dessineCartes();
    this.dessineListe();
    this.dessineMontee(plein);
    this.dessineAdditionnel(plein);
    this.annonceJauge(`${t} grammes sur ${cible}`);
  }

  dessineCartes() {
    const pleine = this.portionsPosees() >= this.capaciteEnPortions();

    this.cartes.forEach((info, handle) => {
      const { el } = info;
      const n = this.etat.portions.get(handle) || 0;
      el.dataset.portions = String(n);

      const compteur = el.querySelector('[data-compteur]');
      if (compteur) compteur.textContent = info.piece ? `×${n}` : `${n * PORTION} g`;

      const moins = el.querySelector('[data-retirer]');
      if (moins) moins.disabled = n === 0;

      const plus = el.querySelector('.mx-bonbon__plus');
      if (plus) plus.disabled = pleine;

      const dit = el.querySelector('[data-etat-sr]');
      if (dit) {
        dit.textContent = n
          ? (info.piece ? `, ${n} pièce${n > 1 ? 's' : ''} dans ta box`
                        : `, ${n * PORTION} grammes dans ta box`)
          : '';
      }
    });
  }

  dessineListe() {
    const liste = this.querySelector('[data-liste]');
    if (!liste) return;

    liste.textContent = '';
    this.etat.ordre.forEach((handle) => {
      const n = this.etat.portions.get(handle);
      const info = this.infos(handle);
      if (!n || !info) return;

      const quantite = info.piece
        ? `${n} pièce${n > 1 ? 's' : ''}`
        : `${n * PORTION} g`;

      const ligne = document.createElement('li');
      ligne.className = 'mx-ligne-mix';
      ligne.innerHTML = `
        <span class="mx-photo mx-ligne-mix__photo">
          <img src="${this.echappe(info.image)}" alt="" width="80" height="80" loading="lazy">
        </span>
        <span class="mx-ligne-mix__nom">${this.echappe(info.nom)}</span>
        <span class="mx-ligne-mix__g">${quantite}</span>
        <span class="mx-ligne-mix__pas">
          <button type="button" data-retirer="${this.echappe(handle)}" aria-label="Retirer une portion de ${this.echappe(info.nom)}">&minus;</button>
          <button type="button" data-ajouter-un="${this.echappe(handle)}" aria-label="Ajouter une portion de ${this.echappe(info.nom)}">+</button>
        </span>`;
      liste.appendChild(ligne);
    });

    const rempli = this.etat.ordre.length > 0;
    this.querySelectorAll('[data-vide]').forEach((el) => (el.hidden = rempli));
  }

  /* Montee en taille, proposee quand la box est pleine : le contenu est
     garde, seule la taille change. */
  dessineMontee(plein) {
    const zone = this.querySelector('[data-montee]');
    if (!zone) return;

    const suivante = this.tailleSuivante();
    zone.hidden = !plein || !suivante;
    if (zone.hidden) return;

    const gagnes = suivante.capacite - (this.taille?.capacite ?? 0);
    const cout = (suivante.prix - (this.taille?.prix ?? 0));
    const texte = this.querySelector('[data-montee-texte]');
    if (texte) {
      texte.textContent = `Ta box est pleine 🍬 Passe à la ${suivante.nom} : `
        + `+${gagnes} g pour ${this.monnaie(cout)} de plus.`;
    }
  }

  tailleSuivante() {
    if (!this.taille || !this.tailles) return null;
    return this.tailles
      .filter((t) => t.capacite > this.taille.capacite)
      .sort((a, b) => a.capacite - b.capacite)[0] ?? null;
  }

  monteDeTaille() {
    const suivante = this.tailleSuivante();
    if (!suivante) return;
    // Plus grande : rien a retirer, donc aucune confirmation a demander.
    this.poseTaille(suivante);
    this.alerte(`Te voilà en ${suivante.nom}.`);
    this.dessine();
    this.sauvegarde();
  }

  /* Additionnel : le mix du moment en 250 g, propose une fois la box
     pleine. Il part dans le panier a part, comme son propre article. */
  dessineAdditionnel(plein) {
    const zone = this.querySelector('[data-additionnel]');
    if (!zone) return;

    const mix = this.mixes.find((m) => m.handle === this.mixDuMoment && m.variante250);
    zone.hidden = !plein || !mix || this.additionnelAjoute;
    if (zone.hidden) return;

    const texte = this.querySelector('[data-additionnel-texte]');
    if (texte) {
      texte.textContent = `Ajoute le mix du moment, ${mix.nom}, 250 g, `
        + `${this.monnaie(mix.prix250)}.`;
    }
  }

  async ajouteAdditionnel() {
    const mix = this.mixes.find((m) => m.handle === this.mixDuMoment && m.variante250);
    if (!mix) return;
    try {
      const rep = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: mix.variante250, quantity: 1 }] }),
      });
      if (!rep.ok) throw new Error(rep.status);
      this.additionnelAjoute = true;
      document.dispatchEvent(new CustomEvent('mx:panier:rafraichir'));
      this.alerte(`${mix.nom} ajouté au panier.`);
      this.dessine();
    } catch (e) {
      console.error('Constructeur : additionnel impossible', e);
      this.alerte("L'ajout a échoué. Réessaie.");
    }
  }

  /* ================= Partir d'un mix ================= */
  ouvreChoix(quoi) {
    const voile = this.querySelector('[data-voile-choix]');
    const panneau = this.querySelector(quoi === 'mix' ? '[data-choix-mix]' : '[data-choix-style]');
    if (!panneau) return;

    if (quoi === 'mix') this.dessineChoixMix();
    else this.dessineChoixStyle();

    if (voile) voile.hidden = false;
    panneau.hidden = false;
    panneau.querySelector('button')?.focus();
  }

  fermeChoix() {
    this.querySelector('[data-voile-choix]')?.setAttribute('hidden', '');
    this.querySelector('[data-choix-mix]')?.setAttribute('hidden', '');
    this.querySelector('[data-choix-style]')?.setAttribute('hidden', '');
  }

  dessineChoixMix() {
    const liste = this.querySelector('[data-liste-mixes]');
    if (!liste) return;
    liste.textContent = '';
    this.mixes.forEach((mix) => {
      const li = document.createElement('li');
      li.innerHTML = `<button class="mx-choix__item" type="button" data-prend-mix="${this.echappe(mix.handle)}">
        <span>${this.echappe(mix.nom)}</span>
        <small>${mix.bonbons.length} bonbons</small>
      </button>`;
      liste.appendChild(li);
    });
  }

  /* Remplit la box avec les bonbons donnes, repartis a parts egales :
     un tour de liste a la fois, jusqu'a ce que la capacite soit atteinte. */
  remplitAvec(handles, remplace) {
    if (!this.taille) return 0;
    if (remplace) {
      this.etat.ordre = [];
      this.etat.portions.clear();
    }

    const capacite = this.capaciteEnPortions();
    let posees = 0;
    let tour = 0;

    // Un tour complet sans rien poser signifie que plus rien ne passe :
    // la liste est vide, ou la box l'est deja.
    while (this.portionsPosees() < capacite) {
      let poseeCeTour = false;
      for (const handle of handles) {
        if (this.portionsPosees() >= capacite) break;
        if (this.obstacle()) continue;
        if (!this.etat.portions.has(handle)) this.etat.ordre.push(handle);
        this.etat.portions.set(handle, (this.etat.portions.get(handle) || 0) + 1);
        posees++;
        poseeCeTour = true;
      }
      if (!poseeCeTour) break;
      if (++tour > capacite) break;
    }
    return posees;
  }

  partirDunMix(handle) {
    const mix = this.mixes.find((m) => m.handle === handle);
    if (!mix) return;
    this.fermeChoix();
    this.remplitAvec(mix.bonbons.map((b) => b.h), true);
    this.alerte(`Box remplie avec ${mix.nom}.`);
    this.dessine();
    this.sauvegarde();
  }

  /* ================= Compléter pour moi ================= */
  dessineChoixStyle() {
    const liste = this.querySelector('[data-liste-styles]');
    const intro = this.querySelector('[data-style-intro]');
    const reste = (this.capaciteEnPortions() - this.portionsPosees()) * PORTION;
    if (intro) intro.textContent = `Il te reste ${reste} g. Choisis une envie.`;
    if (!liste) return;

    liste.textContent = '';
    this.styles.forEach((style, i) => {
      const li = document.createElement('li');
      li.innerHTML = `<button class="mx-choix__item" type="button" data-prend-style="${i}">
        <span>${this.echappe(style.libelle)}</span>
      </button>`;
      liste.appendChild(li);
    });
  }

  async completePourMoi(rang) {
    const style = this.styles[rang];
    if (!style) return;
    this.fermeChoix();

    // Le remplissage pioche dans tout le catalogue : sans index, il ne
    // verrait que les quarante-huit premiers.
    if (!this.indexPret) {
      this.alerte('Un instant, je regarde tout le catalogue…');
      await this.demandeIndex();
    }

    const voulues = style.etiquettes
      ? style.etiquettes.split(',').map((t) => t.trim()).filter(Boolean)
      : [];

    const candidats = this.index.filter((b) => {
      if (!voulues.length) return true;
      const tags = b.t.split(' ');
      return voulues.some((v) => tags.includes(v));
    });

    if (!candidats.length) {
      this.alerte('Aucun bonbon de ce style pour le moment.');
      return;
    }

    const manque = this.capaciteEnPortions() - this.portionsPosees();
    const melange = [...candidats].sort(() => Math.random() - 0.5);
    for (let i = 0; i < manque; i++) {
      const choisi = melange[i % melange.length];
      const handle = choisi.h;
      if (!this.etat.portions.has(handle)) this.etat.ordre.push(handle);
      this.etat.portions.set(handle, (this.etat.portions.get(handle) || 0) + 1);
    }

    this.alerte(`Box complétée · ${style.libelle}.`);
    this.dessine();
    this.sauvegarde();
  }

  /* ================= Recherche et filtres ================= */
  brancheRecherche() {
    const champ = this.querySelector('[data-recherche-mix]');
    if (!champ) return;
    let minuterie;
    champ.addEventListener('input', () => {
      clearTimeout(minuterie);
      this.demandeIndex();
      minuterie = setTimeout(() => {
        this.requete = sansAccent(champ.value.trim());
        this.recompose();
      }, 160);
    });
  }

  brancheFiltres() {
    this.querySelectorAll('[data-filtre]').forEach((bouton) => {
      bouton.addEventListener('click', () => {
        this.demandeIndex();
        const actif = bouton.getAttribute('aria-pressed') === 'true';
        this.querySelectorAll('[data-filtre]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
        bouton.setAttribute('aria-pressed', String(!actif || bouton.dataset.filtre === ''));
        this.filtre = actif && bouton.dataset.filtre !== '' ? '' : bouton.dataset.filtre;
        if (this.filtre === '') {
          this.querySelector('[data-filtre=""]')?.setAttribute('aria-pressed', 'true');
        }
        this.recompose();
      });
    });
  }

  correspond(b) {
    if (this.filtre && !b.t.split(' ').includes(this.filtre)) return false;
    if (this.requete && !sansAccent(b.n).includes(this.requete)) return false;
    return true;
  }

  recompose() {
    if (!this.indexPret) {
      // Sans index, on filtre ce qui est deja affiche.
      this.cartes.forEach(({ el, nom, tags }) => {
        const okFiltre = !this.filtre || tags.split(' ').includes(this.filtre);
        const okTexte = !this.requete || sansAccent(nom).includes(this.requete);
        el.hidden = !(okFiltre && okTexte);
      });
      this.majAucun();
      return;
    }

    this.resultats = this.index.filter((b) => this.correspond(b));
    this.affiches = 0;
    this.grille.textContent = '';
    this.cartes.clear();
    this.ajouteLot();
    this.majAucun();
  }

  ajouteLot() {
    const source = this.resultats ?? this.index;
    const lot = source.slice(this.affiches, this.affiches + this.parLot);

    const fragment = document.createDocumentFragment();
    lot.forEach((b) => fragment.appendChild(this.carte(b)));
    this.grille.appendChild(fragment);

    this.affiches += lot.length;
    this.recenseCartes();
    this.dessine();
    this.majVoirPlus();
  }

  /* Reproduit le balisage de mx-mix-bonbon.liquid. Toute modification
     dans l'un doit etre reportee dans l'autre. */
  carte(b) {
    const el = document.createElement('div');
    el.className = 'mx-bonbon';
    el.dataset.bonbon = '';
    el.dataset.handle = b.h;
    el.dataset.nom = b.n;
    el.dataset.image = b.i;
    el.dataset.tags = b.t;
    el.dataset.portions = '0';
    if (b.u === 1) el.setAttribute('data-piece', '');

    const nom = this.echappe(b.n);
    el.innerHTML = `
      <button class="mx-bonbon__tap" type="button" data-ajouter-un="${this.echappe(b.h)}">
        <span class="mx-photo mx-bonbon__photo">
          <img src="${this.echappe(b.i)}" alt="${nom}" width="300" height="300" loading="lazy">
        </span>
        <span class="mx-bonbon__nom">${nom}</span>
        ${b.u === 1 ? '<span class="mx-bonbon__mention">à la pièce</span>' : ''}
        <span class="mx-sr">Ajouter une portion<span data-etat-sr></span></span>
      </button>
      <div class="mx-bonbon__pas">
        <button class="mx-bonbon__moins" type="button" data-retirer="${this.echappe(b.h)}" aria-label="Retirer une portion de ${nom}" disabled>&minus;</button>
        <span class="mx-bonbon__compteur" data-compteur aria-hidden="true">0</span>
        <button class="mx-bonbon__plus" type="button" data-ajouter-un="${this.echappe(b.h)}" aria-label="Ajouter une portion de ${nom}">+</button>
      </div>`;
    return el;
  }

  majAucun() {
    const visibles = [...this.cartes.values()].filter(({ el }) => !el.hidden).length;
    const aucun = this.querySelector('[data-aucun]');
    if (aucun) aucun.hidden = visibles > 0;
  }

  majVoirPlus() {
    const zone = this.querySelector('[data-plus]');
    if (!zone) return;
    const source = this.resultats ?? this.index;
    // Tant que l'index n'est pas la, on ne sait pas s'il reste du monde.
    zone.hidden = this.indexPret && this.affiches >= source.length;
  }

  /* Une seule relance, et seulement sur les deux plus petites tailles :
     au-dessus, le client a deja choisi de voir grand. */
  estPetiteTaille() {
    if (!this.taille || !this.tailles) return false;
    const triees = [...this.tailles].sort((a, b) => a.capacite - b.capacite);
    return triees.slice(0, 2).some((t) => t.capacite === this.taille.capacite);
  }

  montreLaRelance() {
    const suivante = this.tailleSuivante();
    if (!suivante) return false;
    const texte = this.querySelector('[data-relance-texte]');
    if (texte) {
      const gagnes = suivante.capacite - this.taille.capacite;
      const cout = suivante.prix - this.taille.prix;
      texte.textContent = `Encore un mot : pour ${this.monnaie(cout)} de plus, `
        + `la ${suivante.nom} te donne ${gagnes} g de bonbons en plus.`;
    }
    const panneau = this.querySelector('[data-relance]');
    if (!panneau) return false;
    panneau.hidden = false;
    panneau.querySelector('[data-relance-oui]')?.focus();
    return true;
  }

  repondAlaRelance(monte) {
    const panneau = this.querySelector('[data-relance]');
    if (panneau) panneau.hidden = true;
    if (monte) {
      // La box grandit : elle n'est plus pleine, il reste a la remplir.
      this.monteDeTaille();
      return;
    }
    this.ajouteAuPanier();
  }

  /* ================= Panier ================= */
  async ajouteAuPanier() {
    const bouton = this.querySelector('[data-ajouter]');
    if (!this.taille) return;
    if (this.portionsPosees() < this.capaciteEnPortions()) {
      const reste = (this.capaciteEnPortions() - this.portionsPosees()) * PORTION;
      this.alerte(`Il te reste ${reste} g à choisir.`);
      this.ouvreChoix('style');
      return;
    }

    if (!this.relanceFaite && this.estPetiteTaille()) {
      this.relanceFaite = true;
      if (this.montreLaRelance()) return;
    }

    const libelle = bouton?.textContent;
    if (bouton) { bouton.disabled = true; bouton.textContent = 'Ajout…'; }

    // « Ton mix » est lisible par le client et par la preparation ;
    // « _mix » porte la meme chose en JSON, masque par le soulignement.
    // Les bonbons a la piece sont comptes en pieces, pas en grammes :
    // c'est ce que la personne qui prepare doit lire.
    const lisible = this.etat.ordre.map((h) => {
      const info = this.infos(h);
      const n = this.etat.portions.get(h);
      const q = info?.piece ? `${n} pièce${n > 1 ? 's' : ''}` : `${n * PORTION} g`;
      return `${info?.nom ?? h} (${q})`;
    }).join(', ');

    const technique = this.etat.ordre.map((h) => {
      const info = this.infos(h);
      const n = this.etat.portions.get(h);
      return info?.piece
        ? { handle: h, pieces: n }
        : { handle: h, grammes: n * PORTION };
    });

    const proprietes = { 'Ton mix': lisible, _mix: JSON.stringify(technique) };
    if (this.taille.bonus > 0) {
      proprietes.Bonus = `+${this.taille.bonus} g offerts`;
    } else {
      proprietes.Bonus = 'Un bonbon surprise';
    }

    try {
      const rep = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(this.taille.id), quantity: 1, properties: proprietes }],
        }),
      });
      if (!rep.ok) throw new Error(rep.status);

      document.dispatchEvent(new CustomEvent('mx:panier:rafraichir'));
      document.dispatchEvent(new CustomEvent('mx:panier:ouvrir'));

      // Limite connue : le stock de chaque bonbon n'est pas decremente
      // par une commande de box. Seule la variante de la box l'est. La
      // correction passera par une application de lots ou par une
      // fonction Shopify, qui liront la propriete _mix posee ci-dessus.
      // L'emplacement est ici, rien n'est code.
      document.dispatchEvent(new CustomEvent('mx:mix:ajoute', {
        detail: { variante: this.taille.id, bonbons: technique },
      }));

      this.vide();
      this.alerte('Ta box est dans le panier.');
    } catch (e) {
      console.error('Constructeur : ajout au panier impossible', e);
      this.alerte("L'ajout au panier a échoué. Réessaie.");
      if (bouton) { bouton.textContent = libelle; bouton.disabled = false; }
    }
  }

  /* ================= Sauvegarde locale ================= */
  sauvegarde() {
    try {
      localStorage.setItem(CLE, JSON.stringify({
        capacite: this.taille?.capacite ?? 0,
        box: this.etat.ordre.map((h) => ({ handle: h, portions: this.etat.portions.get(h) })),
      }));
    } catch {
      // Navigation privee ou quota plein : on continue sans sauvegarder.
    }
  }

  restaure() {
    try {
      const brut = localStorage.getItem(CLE);
      if (!brut) return false;
      const data = JSON.parse(brut);

      const taille = this.tailles?.find((t) => t.capacite === data.capacite);
      if (taille) this.poseTaille(taille);

      (data.box || []).forEach(({ handle, portions }) => {
        if (!handle || !portions) return;
        this.etat.ordre.push(handle);
        this.etat.portions.set(handle, portions);
      });
      return this.etat.ordre.length > 0;
    } catch {
      // Donnees illisibles : on repart d'une box vide.
      return false;
    }
  }

  preAjoutParUrl() {
    const demande = new URLSearchParams(location.search).get('add');
    if (!demande || this.etat.portions.has(demande)) return false;
    if (this.obstacle()) return false;
    this.etat.ordre.push(demande);
    this.etat.portions.set(demande, 1);
    return true;
  }

  /* ================= Retours ================= */
  faisTomber(depuis, vers) {
    if (sobre() || !depuis || !vers) return;
    const d = depuis.getBoundingClientRect();
    const v = vers.getBoundingClientRect();

    const bille = document.createElement('span');
    bille.className = 'mx-chute';
    const img = depuis.querySelector('img');
    if (img) bille.style.backgroundImage = `url('${img.currentSrc || img.src}')`;
    bille.style.left = `${d.left + d.width / 2}px`;
    bille.style.top = `${d.top + d.height / 2}px`;
    bille.style.setProperty('--dx', `${v.left + v.width / 2 - (d.left + d.width / 2)}px`);
    bille.style.setProperty('--dy', `${v.top + v.height / 2 - (d.top + d.height / 2)}px`);
    document.body.appendChild(bille);
    bille.addEventListener('animationend', () => bille.remove());
  }

  rebondis(el) {
    if (sobre() || !el) return;
    el.classList.remove('mx-rebond');
    void el.offsetWidth;
    el.classList.add('mx-rebond');
  }

  annonceJauge(texte) {
    const cible = this.querySelector('[data-annonce-jauge]');
    if (cible) cible.textContent = texte;
  }

  alerte(texte) {
    const visible = this.querySelector('[data-message]');
    if (visible) {
      visible.textContent = texte;
      clearTimeout(this.minuterieMessage);
      this.minuterieMessage = setTimeout(() => { visible.textContent = ''; }, 2600);
    }
    const sr = this.querySelector('[data-annonce-alerte]');
    if (sr) sr.textContent = texte;
  }

  echappe(s) {
    return String(s ?? '').replace(/[<>&"]/g, (c) =>
      ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]);
  }

  monnaie(centimes) {
    const format = this.format;
    if (!format) return (centimes / 100).toFixed(2);
    const virgule = (n) => (n / 100).toFixed(2).replace('.', ',');
    const point = (n) => (n / 100).toFixed(2);
    const entier = (n) => String(Math.round(n / 100));
    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, jeton) => {
      if (jeton.startsWith('amount_no_decimals')) return entier(centimes);
      if (jeton.includes('comma_separator')) return virgule(centimes);
      return point(centimes).replace('.', ',');
    }).trim();
  }
}

customElements.define('mx-mix', MxMix);
